import {Banner, Label, LoadingSpinner, Text, Toggle} from '@albert-io/atomic';
import classNames from 'classnames';
import React, {useCallback, useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useQuestionTranslationStore} from './TranslationStore';

import './translation-toggle.scss';

interface TranslationHeaderProps {
  showTranslations: boolean;
  onToggle: () => void;
  translationCoverage?: 'none' | 'full' | 'partial';
  fetchingTranslations?: boolean; // TODO: implment loading symbol or something
  inLegacyView?: boolean;
}

const TranslationHeader = ({
  showTranslations,
  onToggle,
  translationCoverage = 'none',
  fetchingTranslations,
  inLegacyView = false
}: TranslationHeaderProps) => {
  const renderBannerRow = useCallback(() => {
    if (fetchingTranslations) {
      return <LoadingSpinner bottomMarginLevel={0} className='u-mar-lr_auto' />;
    }
    const noTranslation = translationCoverage === 'none';
    const hasSubText = translationCoverage === 'partial';
    const mainText = translationCoverage === 'none' ? translationText.none : translationText.es;
    const mainTextColor = translationCoverage === 'none' ? 'tertiary' : 'info';

    return (
      <>
        <Banner.Icon icon={['fal', 'language']} />
        <Text size='s' color={showTranslations ? mainTextColor : undefined}>
          {mainText}
        </Text>
        {hasSubText && (
          <Text
            size='s'
            className='u-mar-l_auto '
            italic
            color={showTranslations ? 'inherit' : 'tertiary'}
          >
            {translationText.partial}
          </Text>
        )}
        <Toggle
          size='xs'
          disabled={noTranslation}
          checked={showTranslations}
          className={hasSubText ? togglePadding.subtext : togglePadding.noSubtext}
          onChange={onToggle}
        />
      </>
    );
  }, [fetchingTranslations, showTranslations, onToggle, translationCoverage]);

  const noTranslation = translationCoverage === 'none';

  return (
    <Label tabIndex={0} onClick={(e) => noTranslation && e.preventDefault()}>
      <Banner
        type={showTranslations ? 'info' : 'neutral'}
        className={classNames('translation-toggle', {
          'translation-toggle--legacy': inLegacyView
        })}
        cardClassName={classNames('translation-toggle__card', {
          'translation-toggle__card--legacy': inLegacyView,
          'u-pointer-events_none': noTranslation
        })}
        border={false}
      >
        {renderBannerRow()}
      </Banner>
    </Label>
  );
};

const translationText = {
  none: 'Translations not available for this section',
  es: 'Translate to Spanish',
  partial: 'Partially available'
};

const togglePadding = {
  subtext: 'u-mar-l_1',
  noSubtext: 'u-mar-l_auto'
};

type TranslationWrapperProps = Omit<
  TranslationHeaderProps,
  'translationCoverage' | 'showTranslations' | 'onToggle'
>;

const TranslationHeaderWrapper = (props: TranslationWrapperProps) => {
  const {fetchingTranslations, translationCoverage, showTranslations, setShowTranslations} =
    useQuestionTranslationStore(
      useShallow((state) => ({
        fetchingTranslations: state.fetchingTranslations,
        translationCoverage: state.translationCoverage,
        showTranslations: state.showTranslations,
        setShowTranslations: state.onToggle
      }))
    );

  const showTranslationToggle = useShowTranslationToggle();

  if (!showTranslationToggle) return null;

  return (
    <>
      <TranslationHeader
        showTranslations={showTranslations}
        onToggle={setShowTranslations}
        inLegacyView={props.inLegacyView}
        translationCoverage={translationCoverage}
        fetchingTranslations={fetchingTranslations}
      />
      {showTranslations && (
        <Banner
          className='translation-toggle--warning'
          border={false}
          type='warning'
          cardClassName={classNames('translation-toggle__card--warning', {
            'translation-toggle__card--warning--legacy': props.inLegacyView
          })}
        >
          <Banner.Icon className='u-mar-l_auto' icon={['fal', 'hand']} />
          <Text color='inherit' size='s' className='u-mar-r_auto'>
            {warningText}
          </Text>
        </Banner>
      )}
    </>
  );
};

const warningText =
  'La respuesta se calificará según el texto en inglés; revisa tus respuestas en inglés.';

export default TranslationHeaderWrapper;

export const useShowTranslationToggle = () => {
  const {isReady, hasTranslationAccess, useAssignmentSettings, assignmentSettings} =
    useQuestionTranslationStore(
      useShallow((state) => ({
        isReady: state.isReady,
        hasTranslationAccess: state.hasTranslationAccess,
        useAssignmentSettings: state.useAssignmentSettings,
        assignmentSettings: state.assignmentSettings
      }))
    );
  const showTranslationToggle = useMemo(() => {
    if (isReady && hasTranslationAccess) {
      if (!useAssignmentSettings) {
        // don't worry about assignment settings if flag is off
        return true;
      }
      const {assignment, classroom, student} = assignmentSettings;

      return hasAssignmentSettingAccess(assignment, classroom, student);
    }
    return false;
  }, [isReady, hasTranslationAccess, useAssignmentSettings, assignmentSettings]);

  return showTranslationToggle;
};

export const hasAssignmentSettingAccess = (
  assignment: boolean,
  classroom: boolean,
  student: boolean
) => {
  // all translation settings are on
  if (assignment && classroom && student) {
    return true;
  }
  // has class overide to true, and student not set to false
  if (!assignment && classroom && student) {
    return true;
  }
  // has student override set to true
  if ((!assignment && !classroom && student) || (assignment && !classroom && student)) {
    return true;
  }
  return false;
};
